if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      hide() {
        if (this.mobileZoom && this.zoomImage) this.syncGalleryImage();
        this.stopMobileZoom();
        super.hide();
      }

      show(opener) {
        this.mobileZoom = !!opener.closest('product-info.hss-mobile-product') && matchMedia('(max-width: 749px)').matches;
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.querySelectorAll(`[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`)
          .forEach((element) => element.classList.remove('active'));
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        if (this.mobileZoom && activeMedia.tagName === 'IMG') {
          this.startMobileZoom(activeMedia);
          return;
        }
        this.mobileZoom = false;
        activeMedia.scrollIntoView();
        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        if (activeMedia.nodeName === 'DEFERRED-MEDIA' && activeMediaContent && activeMediaContent.querySelector('.js-youtube')) {
          activeMedia.loadContent();
        }
      }

      startMobileZoom(image) {
        this.classList.add('hss-mobile-zoom');
        this.zoomImage = image;
        this.stage = this.querySelector('[role="document"]');
        this.zoomImages = [...this.stage.querySelectorAll(':scope > img[data-media-id]')];
        this.pointers = new Map();
        this.lastTap = null;
        this.lastTouch = 0;
        this.setupZoomEvents();
        this.stage.scrollTop = this.stage.scrollLeft = 0;
        this.fitImage();
        this.updateImageCounter();
        this.zoomResize = new ResizeObserver(() => {
          if (!matchMedia('(max-width: 749px)').matches) this.hide();
          else this.fitImage();
        });
        this.zoomResize.observe(this.stage);
        this.imageLoad = () => this.fitImage();
        image.addEventListener('load', this.imageLoad);
      }

      setupZoomEvents() {
        if (this.zoomEventsReady) return;
        this.zoomEventsReady = true;
        const dialog = this.querySelector('[role="dialog"]');
        const tools = document.createElement('div');
        tools.className = 'hss-mobile-zoom-tools';
        this.zoomButton = document.createElement('button');
        this.zoomButton.type = 'button';
        this.zoomButton.setAttribute('aria-label', 'Fit image');
        this.zoomButton.addEventListener('click', () => this.fitImage());
        this.zoomOutButton = document.createElement('button');
        this.zoomOutButton.type = 'button';
        this.zoomOutButton.textContent = '−';
        this.zoomOutButton.setAttribute('aria-label', 'Zoom out');
        this.zoomOutButton.addEventListener('click', () => this.zoomAt(Math.max(1, this.scale - 0.5), { x: 0, y: 0 }));
        this.zoomInButton = document.createElement('button');
        this.zoomInButton.type = 'button';
        this.zoomInButton.textContent = '+';
        this.zoomInButton.setAttribute('aria-label', 'Zoom in');
        this.zoomInButton.addEventListener('click', () => this.zoomAt(Math.min(4, this.scale + 0.5), { x: 0, y: 0 }));
        // The theme's desktop modal closes on mouse pointerup; mobile tools must not.
        tools.addEventListener('pointerup', (event) => event.stopPropagation());
        tools.append(this.zoomOutButton, this.zoomButton, this.zoomInButton);
        dialog.append(tools);
        this.imageNav = document.createElement('div');
        this.imageNav.className = 'hss-mobile-zoom-nav';
        this.imageNav.addEventListener('pointerup', (event) => event.stopPropagation());
        for (const [direction, label, symbol] of [[-1, 'Previous image', '‹'], [1, 'Next image', '›']]) {
          const button = document.createElement('button');
          button.type = 'button';
          button.textContent = symbol;
          button.setAttribute('aria-label', label);
          button.className = direction < 0 ? 'hss-zoom-prev' : 'hss-zoom-next';
          button.addEventListener('click', () => this.changeImage(direction));
          this.imageNav.append(button);
        }
        dialog.append(this.imageNav);
        this.imageCounter = document.createElement('span');
        this.imageCounter.className = 'hss-mobile-zoom-counter';
        this.imageCounter.setAttribute('role', 'status');
        this.imageCounter.setAttribute('aria-live', 'polite');
        dialog.append(this.imageCounter);
        const hint = document.createElement('p');
        hint.className = 'hss-mobile-zoom-hint';
        hint.textContent = 'Pinch or double-tap to zoom. Drag to move.';
        dialog.append(hint);
        this.stage.addEventListener('dragstart', (event) => { if (this.mobileZoom) event.preventDefault(); });
        this.stage.addEventListener('pointerdown', (event) => this.zoomPointerDown(event));
        this.stage.addEventListener('pointermove', (event) => this.zoomPointerMove(event));
        this.stage.addEventListener('pointerup', (event) => this.zoomPointerEnd(event));
        this.stage.addEventListener('pointercancel', (event) => this.zoomPointerEnd(event));
        this.stage.addEventListener('dblclick', (event) => {
          if (!this.mobileZoom) return;
          event.preventDefault();
          if (Date.now() - this.lastTouch > 600) this.toggleZoom(this.zoomPoint(event));
        });
        this.stage.addEventListener('keydown', (event) => {
          if (!this.mobileZoom) return;
          if (event.key === '+' || event.key === '=') this.zoomAt(Math.min(4, this.scale + 0.5), { x: 0, y: 0 });
          else if (event.key === '-') this.zoomAt(Math.max(1, this.scale - 0.5), { x: 0, y: 0 });
          else if (event.key === '0') this.fitImage();
          else if (event.key === 'ArrowLeft') this.changeImage(-1);
          else if (event.key === 'ArrowRight') this.changeImage(1);
          else return;
          event.preventDefault();
        });
      }

      zoomPoint(event) {
        const r = this.stage.getBoundingClientRect();
        return { x: event.clientX - r.left - r.width / 2, y: event.clientY - r.top - r.height / 2 };
      }

      fitImage() {
        if (!this.mobileZoom || !this.zoomImage) return;
        const image = this.zoomImage;
        const width = image.naturalWidth || Number(image.getAttribute('width'));
        const height = image.naturalHeight || Number(image.getAttribute('height'));
        const fit = Math.min(this.stage.clientWidth / width, this.stage.clientHeight / height, 1);
        this.fitWidth = width * fit;
        this.fitHeight = height * fit;
        image.style.width = `${this.fitWidth}px`;
        image.style.height = `${this.fitHeight}px`;
        this.scale = 1;
        this.panX = this.panY = 0;
        this.pointers.clear();
        this.lastTap = null;
        this.renderZoom();
      }

      renderZoom() {
        const limitX = Math.max(0, (this.fitWidth * this.scale - this.stage.clientWidth) / 2);
        const limitY = Math.max(0, (this.fitHeight * this.scale - this.stage.clientHeight) / 2);
        this.panX = Math.max(-limitX, Math.min(limitX, this.panX));
        this.panY = Math.max(-limitY, Math.min(limitY, this.panY));
        this.zoomImage.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.scale})`;
        this.zoomButton.textContent = 'Fit';
        this.zoomOutButton.disabled = this.scale <= 1.01;
        this.zoomInButton.disabled = this.scale >= 4;
        this.stage.style.cursor = this.scale > 1.01 ? 'grab' : 'zoom-in';
      }

      zoomAt(scale, point) {
        const ratio = scale / this.scale;
        this.panX = point.x - (point.x - this.panX) * ratio;
        this.panY = point.y - (point.y - this.panY) * ratio;
        this.scale = scale;
        this.renderZoom();
      }

      toggleZoom(point) {
        if (this.scale > 1.01) this.fitImage();
        else this.zoomAt(2.5, point);
      }

      gestureStart() {
        const points = [...this.pointers.values()];
        this.gesture = { scale: this.scale, x: this.panX, y: this.panY, first: points[0] };
        if (points.length >= 2) {
          this.gesture.distance = Math.max(1, Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y));
          this.gesture.mid = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
        }
      }

      zoomPointerDown(event) {
        if (!this.mobileZoom || (event.pointerType === 'mouse' && event.button !== 0)) return;
        const point = this.zoomPoint(event);
        if (!this.pointers.size) {
          this.tapStart = { ...point, time: Date.now() };
          this.gestureMoved = false;
        } else {
          this.gestureMoved = true;
          this.lastTap = null;
        }
        this.pointers.set(event.pointerId, point);
        this.stage.setPointerCapture(event.pointerId);
        this.gestureStart();
      }

      zoomPointerMove(event) {
        if (!this.mobileZoom || !this.pointers.has(event.pointerId)) return;
        const point = this.zoomPoint(event);
        this.pointers.set(event.pointerId, point);
        if (Math.hypot(point.x - this.tapStart.x, point.y - this.tapStart.y) > 8) this.gestureMoved = true;
        const points = [...this.pointers.values()];
        const start = this.gesture;
        if (points.length >= 2) {
          const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
          this.scale = Math.max(1, Math.min(4, start.scale * distance / start.distance));
          const ratio = this.scale / start.scale;
          this.panX = (points[0].x + points[1].x) / 2 - (start.mid.x - start.x) * ratio;
          this.panY = (points[0].y + points[1].y) / 2 - (start.mid.y - start.y) * ratio;
        } else if (this.scale > 1) {
          this.panX = start.x + point.x - start.first.x;
          this.panY = start.y + point.y - start.first.y;
        }
        event.preventDefault();
        this.renderZoom();
      }

      zoomPointerEnd(event) {
        if (!this.mobileZoom) return;
        event.stopPropagation();
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.delete(event.pointerId);
        if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
        const now = Date.now();
        if (event.pointerType !== 'mouse') this.lastTouch = now;
        const point = this.zoomPoint(event);
        if (event.type === 'pointerup' && event.pointerType !== 'mouse' && !this.gestureMoved && now - this.tapStart.time < 300) {
          if (this.lastTap && now - this.lastTap.time < 350 && Math.hypot(point.x - this.lastTap.x, point.y - this.lastTap.y) < 30) {
            this.toggleZoom(point);
            this.lastTap = null;
          } else this.lastTap = { ...point, time: now };
        } else this.lastTap = null;
        if (this.pointers.size) this.gestureStart();
      }

      updateImageCounter() {
        this.imageCounter.textContent = `${this.zoomImages.indexOf(this.zoomImage) + 1} / ${this.zoomImages.length}`;
        this.imageNav.hidden = this.zoomImages.length < 2;
      }

      changeImage(direction) {
        if (!this.mobileZoom || this.zoomImages.length < 2) return;
        const index = (this.zoomImages.indexOf(this.zoomImage) + direction + this.zoomImages.length) % this.zoomImages.length;
        this.zoomImage.removeEventListener('load', this.imageLoad);
        this.zoomImage.classList.remove('active');
        for (const property of ['width', 'height', 'transform']) this.zoomImage.style.removeProperty(property);
        this.zoomImage = this.zoomImages[index];
        this.zoomImage.classList.add('active');
        this.zoomImage.addEventListener('load', this.imageLoad);
        this.fitImage();
        this.updateImageCounter();
      }

      syncGalleryImage() {
        const product = this.openedBy?.closest('product-info.hss-mobile-product');
        const mediaId = this.zoomImage.getAttribute('data-media-id');
        const button = product?.querySelector(`.product__media-toggle[data-media-id="${mediaId}"]`);
        const slide = button?.closest('.product__media-item');
        if (!slide) return;
        const list = slide.parentElement;
        list.querySelectorAll('.product__media-item').forEach((item) => item.classList.toggle('is-active', item === slide));
        list.scrollTo({ left: slide.offsetLeft, behavior: 'instant' });
        this.openedBy = button;
      }

      stopMobileZoom() {
        this.zoomResize?.disconnect();
        if (this.zoomImage) {
          this.zoomImage.removeEventListener('load', this.imageLoad);
          this.zoomImage.style.removeProperty('width');
          this.zoomImage.style.removeProperty('height');
          this.zoomImage.style.removeProperty('transform');
        }
        this.pointers?.clear();
        this.stage?.style.removeProperty('cursor');
        this.classList.remove('hss-mobile-zoom');
        this.mobileZoom = false;
        this.zoomImage = null;
      }
    }
  );
}
